import React, { useMemo } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../shared/animationVariants';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { getGuardTelemetry } from '../../../practice';
import { Activity, ShieldAlert, BarChart2, AlertTriangle, Target } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
export function SessionAnalyticsPage() {
  const {
    accounts,
    getLearnerData
  } = useAppStore();
  const data = useMemo(() => {
    const allAccounts = accounts?.accountsById ? Object.values(accounts.accountsById) : [];
    const learners = allAccounts.filter(a => a.role === 'learner');
    let totalAttempts = 0;
    let correctAttempts = 0;
    let frustrationEvents = 0;
    const tierCounts: Record<number, number> = {};
    learners.forEach(learner => {
      const lData = getLearnerData(learner.id);
      if (!lData?.familyMastery) return;
      Object.values(lData.familyMastery).forEach(mastery => {
        if (!mastery.history) return;
        let consecutiveErrors = 0;
        mastery.history.forEach(attempt => {
          totalAttempts++;
          if (attempt.isCorrect) {
            correctAttempts++;
            consecutiveErrors = 0;
          } else {
            consecutiveErrors++;
            if (consecutiveErrors === 3) {
              frustrationEvents++;
            }
          }
          if (attempt.tier) {
            tierCounts[attempt.tier] = (tierCounts[attempt.tier] || 0) + 1;
          }
        });
      });
    });
    const guardData = getGuardTelemetry();
    return {
      totalAttempts,
      accuracy: totalAttempts > 0 ? Math.round(correctAttempts / totalAttempts * 100) : 0,
      frustrationEvents,
      tierCounts,
      guardData
    };
  }, [accounts, getLearnerData]);
  const maxTierCount = Math.max(...Object.values(data.tierCounts), 1);
  if (data.totalAttempts === 0 && data.guardData.totalSessions === 0) {
    return <PageLayout title="Sessie Analytics" subtitle="Inzicht in het gedrag van de Adaptive Engine (P-15)" data-id="element-7401" data-mp="1t28dxxnbjm5a">
        
        <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-7402" data-mp="26twlroqjbogg">
          <BarChart2 className="w-12 h-12 text-slate-300 mb-4" data-id="element-7403" data-mp="222wryscppmi6" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2" data-id="element-7404" data-mp="220k11poz81sz">
            Nog geen sessiedata
          </h3>
          <p className="text-sm text-slate-500 max-w-md" data-id="element-7405" data-mp="2bsgcs3jzjqfo">
            Zodra leerlingen oefensessies voltooien, verschijnen hier
            statistieken over accuratesse, tier distributie en guard telemetrie.
          </p>
        </div>
      </PageLayout>;
  }
  return <PageLayout title="Sessie Analytics" subtitle="Inzicht in het gedrag van de Adaptive Engine (P-15)" data-id="element-7406" data-mp="2ktnzdod532tx">
      
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-7407" data-mp="vegf6z8m4cvl">
        
        <motion.div variants={itemVariants} data-id="element-7408" data-mp="32273743yowa9">
          <StatCard label="Gemiddelde Accuratesse" value={`${data.accuracy}%`} icon={<Target className="w-4 h-4 text-emerald-500" data-id="element-7410" data-mp="3711t0q0cjl3v" />} data-id="element-7409" data-mp="324jqlwshnyup" />
          
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-7411" data-mp="2t9dgwmq3kwmw">
          <StatCard label="Frustratie Events (P-12)" value={data.frustrationEvents} icon={<AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-7413" data-mp="2x9nmvzdw3n1r" />} data-id="element-7412" data-mp="2xbqn1c8rawk7" />
          
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-7414" data-mp="2jao1d0lobo9t">
          <StatCard label="Guard Rejections (P-3)" value={data.guardData.totalFatalRejections} icon={<ShieldAlert className="w-4 h-4 text-rose-500" data-id="element-7416" data-mp="2abkf5nr7msr3" />} data-id="element-7415" data-mp="2oblg137h606k" />
          
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-7417" data-mp="2ecmgca4otevp">
          <StatCard label="Totaal Vragen" value={data.totalAttempts} icon={<Activity className="w-4 h-4 text-blue-500" data-id="element-7419" data-mp="8jee0rnwtdr5" />} data-id="element-7418" data-mp="2ednuwokwwji6" />
          
        </motion.div>
      </motion.div>

      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-7420" data-mp="clsx9cvhqfiy">
        
        <motion.div variants={itemVariants} data-id="element-7421" data-mp="3jy6nwp2bg7bh">
          <Card className="p-5 h-full" data-id="element-7422" data-mp="3f0n130rjym7x">
            <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-7423" data-mp="3f1prl7kyqmnb">
              <BarChart2 className="w-4 h-4 text-indigo-500" data-id="element-7424" data-mp="3a0if4v7vkzab" />
              Tier Distributie
            </h3>
            <div className="space-y-2" data-id="element-7425" data-mp="352qpk7updp9a">
              {Array.from({
              length: 16
            }, (_, i) => i + 1).map(tier => {
              const count = data.tierCounts[tier] || 0;
              const pct = count / maxTierCount * 100;
              if (count === 0 && tier > 8) return null; // Hide empty higher tiers to save space
              return <div key={tier} className="flex items-center gap-3" data-id="element-7426" data-mp="2md0stf7dqteq">
                    <div className="w-12 text-xs font-medium text-slate-500 text-right" data-id="element-7427" data-mp="2hdeibdixrr9m">
                      Tier {tier}
                    </div>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-7428" data-mp="2dd4114pp1efg">
                      <div className="h-full bg-indigo-500 rounded-full" style={{
                    width: `${pct}%`
                  }} data-id="element-7429" data-mp="ssqkg70pusz0" />
                      
                    </div>
                    <div className="w-10 text-xs font-semibold text-slate-700" data-id="element-7430" data-mp="11u6vmgzcuz4a">
                      {count}
                    </div>
                  </div>;
            })}
            </div>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants} data-id="element-7431" data-mp="97dpfj3757mw">
          <Card className="p-5 h-full" data-id="element-7432" data-mp="e4vs4rqmeqmg">
            <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-7433" data-mp="e5nmjz8heib5">
              <ShieldAlert className="w-4 h-4 text-rose-500" data-id="element-7434" data-mp="j2xilxqdls7y" />
              Pedagogical Guard (Top Redenen)
            </h3>
            {data.guardData.topReasons.length > 0 ? <div className="space-y-4" data-id="element-7435" data-mp="3mkyxury6a585">
                {data.guardData.topReasons.map((reason, i) => <div key={i} data-id="element-7436" data-mp="3rjscwot3y4af" data-mpi={i}>
                    <div className="flex justify-between text-sm mb-1" data-id="element-7437" data-mp="3rj0el684lsgg">
                      <span className="font-medium text-slate-700" data-id="element-7438" data-mp="3jj2r6at554cf">
                        {reason.reason}
                      </span>
                      <span className="font-bold text-slate-900" data-id="element-7439" data-mp="3fgg04c4annga">
                        {reason.count}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-7440" data-mp="3o9swjo1kh774">
                      <div className="h-full bg-rose-500 rounded-full" style={{
                  width: `${data.guardData.totalFatalRejections > 0 ? reason.count / data.guardData.totalFatalRejections * 100 : 0}%`
                }} data-id="element-7441" data-mp="3oc5fo4j29nlr" />
                  
                    </div>
                  </div>)}
                <div className="pt-4 mt-4 border-t border-slate-100" data-id="element-7442" data-mp="3ag1qipn1o5g5">
                  <div className="flex justify-between text-sm" data-id="element-7443" data-mp="35hqhgh4kpyyz">
                    <span className="text-slate-500" data-id="element-7444" data-mp="prdxnc4waqjy">
                      Vervangingsratio (Success)
                    </span>
                    <span className="font-bold text-emerald-600" data-id="element-7445" data-mp="kskhfby84lg3">
                      {Math.round(data.guardData.replacementSuccessRate * 100)}%
                    </span>
                  </div>
                </div>
              </div> : <div className="text-sm text-slate-500 text-center py-8" data-id="element-7446" data-mp="27nfor66rytax">
                Geen guard rejections geregistreerd.
              </div>}
          </Card>
        </motion.div>
      </motion.div>
    </PageLayout>;
}